import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings, Puzzle, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { supabase, logoutAll } from '../lib/supabase';
import { toast } from 'sonner';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { SemanticSearchStatus } from './SemanticSearchStatus';

interface UserMenuProps {
  onLogout: () => void;
}

export function UserMenu({ onLogout }: UserMenuProps) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [semanticSearchOpen, setSemanticSearchOpen] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const handleLogout = async () => {
    try {
      await logoutAll();
      toast.success('로그아웃했어요');
      onLogout();
    } catch (error: unknown) {
      console.error('Logout error:', error);
      toast.error('문제가 생겼어요');
    }
  };

  if (!user) return null;

  const initials = user.email?.substring(0, 2).toUpperCase() || 'U';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 rounded-full p-0 hover:bg-primary/10">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <div className="flex items-center gap-2.5 px-2 py-1.5">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user.email}</p>
            <p className="text-xs text-muted-foreground">사용자</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/extension" className="flex items-center w-full">
            <Puzzle className="w-3.5 h-3.5 mr-2" />
            확장 프로그램
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setSemanticSearchOpen(true)}>
          <Search className="w-3.5 h-3.5 mr-2" />
          의미 검색 설정
        </DropdownMenuItem>
        <DropdownMenuItem disabled>
          <Settings className="w-3.5 h-3.5 mr-2" />
          설정 (준비 중)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive">
          <LogOut className="w-3.5 h-3.5 mr-2" />
          로그아웃
        </DropdownMenuItem>
      </DropdownMenuContent>

      {/* Semantic Search Settings Dialog */}
      <SemanticSearchStatus
        open={semanticSearchOpen}
        onOpenChange={setSemanticSearchOpen}
      />
    </DropdownMenu>
  );
}